'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, CalendarIcon, CoinsIcon, EditIcon, HashIcon, TrashIcon, CopyIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { formatDateTime, formatNumber } from '@/lib/utils'
import { useCurrency, useDeleteCurrency, useCopyToClipboard } from '@/hooks'

type PropsType = {
  id: string
}

const CurrencyShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteCurrencyId, setCurrencyId] = useState<string | null>(null)

  const { data: currency, isLoading, error } = useCurrency(id)
  const { mutate: deleteCurrency, isPending: isDeleting } = useDeleteCurrency()

  const handleDelete = () => {
    setCurrencyId(id)
  }

  const confirmDeleteCurrency = () => {
    if (deleteCurrencyId) {
      deleteCurrency(deleteCurrencyId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['currencies'] })
          toast.success(t('deleted_successfully'))
          router.push('/currencies')
          setCurrencyId(null)
        },
        onError: error => {
          console.error('Error deleting currency:', error)
          toast.error(t('error_deleting'))
          setCurrencyId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <CurrencyShowSkeleton />
  }

  if (error || !currency) {
    return <ContentError error={error} listUrl="/currencies" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/currencies">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('currency_details')}</h1>
            <p className="text-muted-foreground">{t('view_currency_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/currencies/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('edit_currency')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CoinsIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {currency.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(currency.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('sort_order')}</div>
              <Badge variant="outline" className="font-mono text-sm">
                {currency.order}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('code')}</div>
              <Badge variant="outline" className="font-mono text-sm">
                {currency.code}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('name')}</div>
              <Badge variant="outline" className="text-sm">
                {currency.name}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('symbol')}</div>
              <Badge variant="outline" className="font-mono text-sm">
                {currency.symbol}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('is_default')}</div>
              <Badge variant={currency.isDefault ? 'success' : 'outline'}>
                {currency.isDefault ? t('yes') : t('no')}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('status')}</div>
              <Badge variant={currency.active ? 'success' : 'secondary'}>
                {currency.active ? t('active') : t('inactive')}
              </Badge>
            </div>
            {currency.latestRate !== undefined && (
              <div className="space-y-2">
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  {t('latest_rate')}
                </div>
                <Badge variant="outline">{formatNumber(currency.latestRate, locale)}</Badge>
              </div>
            )}
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4" />
                {t('created_at')}
              </div>
              <p className="text-sm">{formatDateTime(currency.createdAt, locale)}</p>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4" />
                {t('updated_at')}
              </div>
              <p className="text-sm">{formatDateTime(currency.updatedAt, locale)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteCurrencyId !== null}
        onCancel={() => setCurrencyId(null)}
        onConfirm={confirmDeleteCurrency}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
